import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    DevicesStore,
    InstallationFeaturesHelpers,
    InstallationFeaturesStore,
    RoomTypes,
} from '@store';
import {
    AddRoom,
    DeviceIds,
    Feature,
    FeatureCommands,
    FeatureNames,
    FeaturePropertyKey,
    RoomCommands,
    RoomProperties,
} from '@vi-iot/client';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { consecutiveSpaceValidator } from 'src/app/shared/validators/consecutive-spaces/consecutive-spaces.validator';
import { CommissioningDeleteRoomConfirmModalComponent } from '..';
import { noWhitespaceValidator, uniqueNameValidator } from '../../../../../shared/common';
import { NoSpecialCharactersValidator } from '../../../../../shared/validators/special-characters/special-characters.validator';
import { CloseEventDialog } from '../commissioning-delete-device-confirm-modal/commissioning-delete-device-confirm-modal.component';
import { CommissioningService } from '../commissioning.service';
@Component({
  templateUrl: './commissioning-add-room-standalone-modal.component.html',
  styleUrls: ['./commissioning-add-room-standalone-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningAddRoomModalStandaloneComponent implements OnInit, OnDestroy {
  // Add New Room Forms
  addNewRoomFormGroup: FormGroup<{
    name: FormControl<string>;
    type: FormControl<string>;
  }>;
  roomTypeOptions: string[];

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  addedRooms;
  newRoom: AddRoom;
  isObng = false;
  readonly roomConstraints = InstallationFeaturesHelpers;

  constructor(
    public commissioningService: CommissioningService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<CommissioningAddRoomModalStandaloneComponent>,
    private dialog: MatDialog,
    public installationFeaturesStore: InstallationFeaturesStore,
    private devicesStore: DevicesStore
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('768px');
    this.dialogRef.addPanelClass('commissioning-add-room-standalone-modal');
    this.roomTypeOptions = Object.keys(RoomTypes);

    this.devicesStore.isSelectedDeviceOBNG$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isObng) => (this.isObng = isObng));

    this.installationFeaturesStore.fixedRooms$
      .pipe(
        distinctUntilChanged((prev, curr) => prev.length === curr.length),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((rooms: Feature<RoomProperties, RoomCommands>[]) => {
        this.addedRooms = rooms;
        this.installationFeaturesStore.updateRooms(this.addedRooms);

        this.constructFormGroup(rooms);

        setTimeout(() => {
          this.cdr.markForCheck();
        }, 0);
      });
  }

  addRoom(): void {
    const roomName = `${this.addNewRoomFormGroup.controls.name.value}`.trim();
    const roomType = RoomTypes[`${this.addNewRoomFormGroup.controls.type.value}`.trim()];

    this.installationFeaturesStore.updateAddRoomInfo({
      name: roomName,
      type: roomType,
    });

    this.installationFeaturesStore.updateFeature(
      FeatureNames.rooms,
      [roomName, roomType],
      null,
      [FeaturePropertyKey.name, FeaturePropertyKey.type],
      FeatureCommands.add,
      null,
      DeviceIds.RoomControl,
      {
        hideSuccessSnackbar: true,
      }
    );

    this.constructFormGroup(this.addedRooms);
  }

  deleteRoom(room: Feature<RoomProperties, RoomCommands>): void {
    if (this.isObng && !InstallationFeaturesHelpers.canRemoveRoom(room)) {
      return;
    }

    const dialogRef = this.dialog.open(CommissioningDeleteRoomConfirmModalComponent, {
      width: '715px',
      panelClass: 'commissioning-delete-room-confirm-dialog',
      data: {
        room,
      },
    });

    dialogRef.afterClosed().subscribe((event: CloseEventDialog) => {
      if (event.confirmed) {
        this.commissioningService.deleteRoom(room);

        if (event.shouldDeactivateSRC) {
          this.commissioningService.deactivateSRC();
          this.dialogRef.close();
        }
      }
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private constructFormGroup(rooms: Feature<RoomProperties, RoomCommands>[]): void {
    if (!this.addNewRoomFormGroup) {
      this.addNewRoomFormGroup = this.formBuilder.group({
        name: new FormControl('', [
          Validators.required,
          noWhitespaceValidator(),
          Validators.maxLength(40),
          uniqueNameValidator(rooms.map((entry) => entry?.properties?.name?.value)),
          NoSpecialCharactersValidator(),
          consecutiveSpaceValidator(),
        ]),
        type: new FormControl(this.roomTypeOptions[0]),
      });
    } else {
      this.addNewRoomFormGroup.controls.name.setValue('');
      this.addNewRoomFormGroup.controls.name.markAsUntouched();
      this.addNewRoomFormGroup.controls.name.markAsPristine();
      this.addNewRoomFormGroup.controls.name.setErrors(null);
      this.addNewRoomFormGroup.controls.type.setValue(this.roomTypeOptions[0]);
    }
  }
}
